import { publicApiClient } from './publicApiClient';
import apiClient from './apiClient';

export interface DownloadResponse {
  success: boolean;
  download_url?: string;
  filename?: string;
  expires_in?: number;
  error?: string;
}

export interface TokenInfoResponse {
  success: boolean;
  remaining_downloads?: number;
  error?: string;
}

/**
 * Get download URL for authenticated users (private download)
 * @param generatedReportId - The generated report ID
 * @param expiry - Optional expiry in seconds (default: 3600)
 */
export async function getReportDownloadUrl(
  generatedReportId: number,
  expiry?: number
): Promise<DownloadResponse> {
  try {
    const params = expiry ? { expiry: expiry.toString() } : {};
    const response = await apiClient.get(`/report-download/${generatedReportId}`, { params });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get download URL',
    };
  }
}

/**
 * Get download URL for public token-based access
 * @param token - The download token
 * @param expiry - Optional expiry in seconds (default: 3600)
 */
export async function getPublicDownloadUrl(
  token: string,
  expiry?: number
): Promise<DownloadResponse> {
  try {
    const params = expiry ? { expiry: expiry.toString() } : {};
    const response = await publicApiClient.get(`/report-download/${token}`, { params });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get download URL',
    };
  }
}

/**
 * Get token info (remaining downloads)
 * @param token - The download token
 */
export async function getTokenInfo(token: string): Promise<TokenInfoResponse> {
  try {
    const response = await publicApiClient.get(`/report-download/${token}/info`);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Invalid token',
    };
  }
}
